import React, {useEffect, useRef, useState} from 'react';
import {changeAvailabilities, getAvailabilities, registerReturn} from "../../../services/enrollments";
import moment from 'moment';
import {extendMoment} from 'moment-range';
import {NotificationManager} from 'react-notifications';
import {Col, Container, Row, Button} from "react-bootstrap";
import {useAuth} from "../../../contexts/authContext";
import {getUnconcludedPaidRegistrations, getUnconcludedUnpaidRegistrations} from "../../../services/registrations";
import {useHistory} from "react-router-dom";
import MySwal from "sweetalert2";
import {doesAbsenceOverlap, isWithinAbsencePeriod} from "../../../utils";
import ScaleLoader from "react-spinners/ScaleLoader";
import {useTranslation} from "react-i18next";

// React Components
import Header from '../../../components/Header/Header';
import LeagueCard from '../../../components/LeagueCard/LeagueCard';
import CollapsibleSelect from '../../../components/CollapsibleSelect/CollapsibleSelect';
import NoRegistrationsAlert from "../../../components/NoRegistrationsAlert";

// CSS Files
import './styles.css';
import {getLeagueDetails} from "../../../services/leagues";

const momentext = extendMoment(moment);

const override = `
    display: block;
    text-align: center;
    margin-top: 10%;
`;

const MainPage = () => {
   const [leagueDetails, setLeagueDetails] = useState({});
   const [gameWeeks, setGameWeeks] = useState([]);
   const [enrollmentAbsence, setEnrollmentAbsence] = useState({});
   const [notAvailable, setNotAvailable] = useState(false);
   const [unconcludedRegistrations, setUnconcludedRegistrations] = useState([]);
   const [loading, setLoading] = useState(true);
   const [sending, setSending] = useState(false);
   const auth = useAuth();
   const history = useHistory();
   const { t } = useTranslation();

   const collapsibleRefs = useRef({});

   useEffect(() => {
      (async () => {
         const leagueId = auth.user.active_enrollment?.league_id;

         if (leagueId) {
            try {
               let leagueDetails = await getLeagueDetails(leagueId);
               setLeagueDetails(leagueDetails);
               setNotAvailable(false);
               await fillAvailabilityWeeksContent(leagueDetails);
            } catch (e) {
               NotificationManager.error(t("common.form.errors.general"), t("common.error"), 4000);
            }
         } else {
            setNotAvailable(true);
         }

         let unconcludedPaidRegistrations = await getUnconcludedPaidRegistrations();
         let unconcludedUnpaidRegistrations = await getUnconcludedUnpaidRegistrations();

         setUnconcludedRegistrations(unconcludedPaidRegistrations.concat(unconcludedUnpaidRegistrations));
         setLoading(false);
      })()
   }, []);

   const fillAvailabilityWeeksContent = async (leagueDetails) => {
      const enrollmentId = auth.user.active_enrollment.id;

      try {
         const start_date = moment(leagueDetails.start_date);
         const end_date = moment(leagueDetails.end_date);
         const date_range = momentext.range(start_date, end_date);

         const game_weeks = [];
         let index = 1;

         for (const week of date_range.by('week')) {
            game_weeks.push({
               number: index,
               start_date: week.format('YYYY-MM-DD'),
               end_date: week.endOf('week').format('YYYY-MM-DD'),
               availability_info: {},
            });
            index++;
         }

         const availWeeks = await getAvailabilities(enrollmentId);

         availWeeks.availabilities.forEach(function (week) {
            game_weeks.forEach(function (game_week) {
               if (game_week.number === week.week_number) {
                  game_week.availability_info = week;
               }
            });
         });
         setGameWeeks(game_weeks)
         setEnrollmentAbsence(availWeeks.enrollment)
      } catch (e) {
         NotificationManager.error(t("common.form.errors.general"), t("common.error"), 4000);
      }
   }

   const sendAvailability = async () => {
      const collapsibleData = {};
      Object.keys(collapsibleRefs.current).forEach(collapsibleSelectNumber => {
         collapsibleData[collapsibleSelectNumber] = collapsibleRefs.current[collapsibleSelectNumber].getData();
      });

      if (verifyAvailabilities(collapsibleData)) {
         setSending(true);
         try {
            await changeAvailabilities(auth.user.active_enrollment.id, collapsibleData);
            NotificationManager.success(t("availabilities_page.success"), t("common.success"), 4000,);
         } catch (e) {
            NotificationManager.error(t("common.form.errors.general"), t("common.error"), 4000);
         } finally {
            setSending(false);
         }
      }
   };

   const verifyAvailabilities = data => {
      let isAcceptable = true;
      const absenceStartDate = new Date(enrollmentAbsence.absence_start_date);
      const absenceEndDate = new Date(enrollmentAbsence.absence_end_date);
      const hasAbsencePeriod = isValidDate(absenceStartDate) && isValidDate(absenceEndDate);

      for (const week in data) {
         const gameWeekStartDate = new Date(collapsibleRefs.current[week].props.week_availability.start_date);
         const gameWeekEndDate = new Date(collapsibleRefs.current[week].props.week_availability.end_date);

         if (!shouldDisableInput(gameWeekStartDate)) {
            if (data[week].availabilities.length) {
               const hasAvailabilityInAbsence = hasAbsencePeriod && data[week].availabilities.some(item => isAvailabilityDayInAbsence(item.day, gameWeekStartDate, absenceStartDate, absenceEndDate));
               const hasEmptyDay = data[week].availabilities.some(item => item.day === '');
               const hasDuplicateDays = data[week].availabilities.some((item, index) => {
                  return data[week].availabilities.some((otherItem, otherIndex) => {
                     return index !== otherIndex && item.day === otherItem.day;
                  });
               });

               if (hasAvailabilityInAbsence || enrollmentAbsence.absence_reason === "Queremos desistir") {
                  isAcceptable = false;
                  NotificationManager.error(t("availabilities_page.errors.absence_collision", {week}), t("common.error"), 4000);
                  break;
               }

               if (!hasEmptyDay && hasDuplicateDays) {
                  isAcceptable = false;
                  NotificationManager.error(t("availabilities_page.errors.same_days", {week}), t("common.error"), 4000);

                  break;
               }

               if (hasAbsencePeriod && doesAbsenceOverlap(absenceStartDate, absenceEndDate, gameWeekStartDate, gameWeekEndDate) && absenceEndDate.getDay() !== 1) {
                  if(hasEmptyDay) {
                     isAcceptable = false;

                     NotificationManager.error(t("availabilities_page.errors.empty", {week}), t("common.error"), 4000);

                     break;
                  }

                  isAcceptable = enoughHoursSelected(data[week].availabilities, week);
                  if(!isAcceptable) break;
               } else {
                  if (hasEmptyDay) {
                     let days = data[week].availabilities.length;
                     isAcceptable = false;

                     NotificationManager.error(`${days < 2 ? t("availabilities_page.errors.minimum_days", {week}) : t("availabilities_page.errors.empty", {week})}`, t("common.error"), 4000);

                     break;
                  }

                  isAcceptable = enoughHoursSelected(data[week].availabilities, week);
                  if(!isAcceptable) break;
               }
            }
         }
      }

      return isAcceptable;
   };

   const isValidDate = date => {
      return date instanceof Date && !isNaN(date.getTime());
   }

   const isAvailabilityDayInAbsence = (day, gameWeekStartDate, absenceStartDate, absenceEndDate) => {
      const dayOffset = {
         "Segunda-feira": 0,
         "Terca-feira": 1,
         "Quarta-feira": 2,
         "Quinta-feira": 3,
         "Sexta-feira": 4,
         "Sabado": 5,
         "Domingo": 6
      }[day];

      if (typeof dayOffset === 'undefined') {
         return false;
      }

      const availabilityDate = new Date(gameWeekStartDate);
      availabilityDate.setDate(availabilityDate.getDate() + dayOffset);

      return isWithinAbsencePeriod(availabilityDate, absenceStartDate, absenceEndDate);
   }

   const enoughHoursSelected = (days, week) => {
      let isAcceptable = true;

      for (let i = 0; i < days.length; i++) {
         if (days[i].day !== '') {
            let selectedAvailabilityHours = 0
            let dayAvailability = Object.values(days[i])

            dayAvailability.splice(0, 4)

            dayAvailability.forEach((availabilityHour) => {
               if (availabilityHour) {
                  selectedAvailabilityHours++
               }
            })

            if (selectedAvailabilityHours < 3) {
               isAcceptable = false

               NotificationManager.error(t("availabilities_page.errors.minimum_hours", {week}), t("common.error"), 4000);
               break;
            }
         }
      }

      return isAcceptable;
   }

   const shouldDisableInput = start_date => {
      const today = moment().format('YYYY-MM-DD');
      const moment_start_date = moment(start_date);

      const remaining_days = moment_start_date.diff(today, 'days');

      return remaining_days < 1;
   };

   const hasManageableAbsence = () => {
      const absenceEndDate = new Date(enrollmentAbsence.absence_end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return enrollmentAbsence.state === "absent" ||
         (enrollmentAbsence.absence_reason && isValidDate(absenceEndDate) && absenceEndDate >= today);
   }

   const headerExplanation = t("availabilities_page.update_deadline");

   return (
      <div>
         <Header headerBarTitle={t("availabilities_page.header")} showHeaderExtras />

         <ScaleLoader
            css={override}
            sizeUnit={"px"}
            size={30}
            color={'#afff55'}
            loading={loading}
         />

         {!loading &&
            <Container>
               {(!auth.user.active_enrollment && unconcludedRegistrations.length === 0)
                  ? <NoRegistrationsAlert
                     text={t("common.no_registrations")}
                  />
                  : <>
                     <Row className={"toplabel"}>
                        <Col xs={12} className={"pb-4"}>
                           <p>{headerExplanation}</p>
                        </Col>
                     </Row>

                     {!loading &&
                        <Row>
                           <Col xs={12} md={{span: 6, offset: 3}}>
                              {leagueDetails.name && (
                                 <LeagueCard
                                    leagueNameClasses="card-games pb-3 mb-3"
                                    leagueName={leagueDetails.name}
                                    leagueDate={`${t("common.date_formatting.from")} ${moment(leagueDetails.start_date).format("DD/MM/YYYY")} ${t("common.date_formatting.to")} ${moment(leagueDetails.end_date).format("DD/MM/YYYY")}`}
                                 >
                                    <p className="start-date-text">{t("availabilities_page.active_league")}</p>
                                 </LeagueCard>
                              )}
                           </Col>

                           {hasManageableAbsence() &&
                              <Col xs={12} md={{span: 6, offset: 3}}>
                                 <div className="alert alert-info" role="alert">
                                    {t("availabilities_page.active_absence", {
                                       interpolation: { escapeValue: false },
                                       reason: enrollmentAbsence.absence_reason,
                                       absenceRange: ["Lesão", "Trabalho/férias"].includes(enrollmentAbsence.absence_reason) ? `${t("common.date_formatting.from")} ${moment(enrollmentAbsence.absence_start_date).format('DD/MM/YYYY')} ${t("common.date_formatting.to")} ${moment(enrollmentAbsence.absence_end_date).format('DD/MM/YYYY')}` : ""
                                    })}
                                 </div>
                              </Col>
                           }

                           {hasManageableAbsence() &&
                              <Col xs={12} md={{span: 6, offset: 3}}>
                                 <Button
                                    onClick={async () => {
                                       await registerReturn(auth.user.active_enrollment.id)
                                       MySwal.fire({
                                          allowOutsideClick: false,
                                          titleText: t("common.success"),
                                          text: t("availabilities_page.return_reported")
                                       }).then(() => {
                                          history.push("/my-games");
                                       });
                                    }}
                                    className="w-100 mb-3"
                                    variant={"primary"}>
                                    {t("availabilities_page.report_return")}
                                 </Button>

                                 <Button onClick={() => history.push("/availabilities/notify_absence", {
                                    leagueEditionStartDate: leagueDetails.start_date,
                                    leagueEditionEndDate: leagueDetails.end_date,
                                    absenceStartDate: enrollmentAbsence.absence_start_date
                                 })} className="w-100 mb-3" variant={"primary"}>
                                    {t("availabilities_page.change_absence")}
                                 </Button>
                              </Col>
                           }

                           {!hasManageableAbsence() && moment(leagueDetails.end_date).diff(moment(), 'days') >= 0 &&
                              <Col xs={12} md={{span: 6, offset: 3}}>
                                 <Button onClick={() => history.push("/availabilities/notify_absence", {
                                    leagueEditionStartDate: leagueDetails.start_date,
                                    leagueEditionEndDate: leagueDetails.end_date
                                 })} className="w-100 mb-3" variant={"primary"}>
                                    {t("availabilities_page.report_absence")}
                                 </Button>
                              </Col>
                           }

                           <Col xs={12}>
                              {notAvailable && (
                                 <h1 className="availabilities-notfound-text">
                                    {t("availabilities_page.waiting_for_league")}
                                 </h1>
                              )}
                           </Col>

                           {gameWeeks.map((week_availability, index) => {
                              return (
                                 <CollapsibleSelect
                                    key={`week${index}`}
                                    week_availability={week_availability}
                                    enrollment={{
                                       ...auth.user.active_enrollment,
                                       ...enrollmentAbsence
                                    }}
                                    ref={ref => (collapsibleRefs.current[week_availability.number] = ref)}
                                 />
                              );
                           })}

                        </Row>
                     }

                     {leagueDetails.name && (
                        <Row>
                           <Col xs={12} sm={{span: 5, offset: 7}} md={{span: 3, offset: 9}}>
                              <Button onClick={() => sendAvailability()} className={"mt-4 mb-4 w-100"} variant="primary" disabled={sending}>{sending ? t("common.please_wait") : t("common.save")}</Button>
                           </Col>
                        </Row>
                     )}
                  </>
               }
            </Container>
         }
      </div>
   );
}

export default MainPage;
